import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api';

/**
 * FAQ API using RTK Query
 *
 * Provides FAQ management endpoints with automatic caching,
 * request deduplication, and loading state management.
 *
 * FAQ entries can be filtered by:
 * - user_id: User-specific FAQs
 * - segmented_knowledge_id: Segment-specific FAQs
 * - No filters: Account-level FAQs
 *
 * Uses centralized baseQuery for consistent error handling.
 */
export const faqApi = createApi({
  reducerPath: 'faqApi',
  baseQuery,
  tagTypes: ['FAQ'],
  // Cache configuration
  keepUnusedDataFor: 300, // 5 minutes cache for FAQ data
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    /**
     * Get list of FAQs with optional filters
     * @param {Object} params - Filter parameters
     * @param {string} [params.userId] - Filter by user
     * @param {string} [params.segmentedKnowledgeId] - Filter by segment
     */
    getFAQs: builder.query({
      query: ({ userId, segmentedKnowledgeId } = {}) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (segmentedKnowledgeId) params.append('segmented_knowledge_id', segmentedKnowledgeId);
        const queryString = params.toString();
        return `/api/v1/faq/${queryString ? `?${queryString}` : ''}`;
      },
      // LIST/ITEM pattern: provide both individual item tags and list tag
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'FAQ', id })),
              { type: 'FAQ', id: 'LIST' },
            ]
          : [{ type: 'FAQ', id: 'LIST' }],
    }),

    /**
     * Get single FAQ by ID
     */
    getFAQById: builder.query({
      query: (id) => `/api/v1/faq/${id}/`,
      providesTags: (result, error, id) => [{ type: 'FAQ', id }],
    }),

    /**
     * Create new FAQ
     */
    createFAQ: builder.mutation({
      query: ({ question, answer, userId, accountId, segmentedKnowledgeId }) => ({
        url: '/api/v1/faq/',
        method: 'POST',
        body: {
          question,
          answer,
          user: userId,
          account: accountId,
          segmented_knowledge: segmentedKnowledgeId,
        },
      }),
      // Only invalidate the LIST tag
      invalidatesTags: [{ type: 'FAQ', id: 'LIST' }],
    }),

    /**
     * Update existing FAQ
     */
    updateFAQ: builder.mutation({
      query: ({ id, question, answer, userId, accountId, segmentedKnowledgeId }) => ({
        url: `/api/v1/faq/${id}/`,
        method: 'PUT',
        body: {
          question,
          answer,
          user: userId,
          account: accountId,
          segmented_knowledge: segmentedKnowledgeId,
        },
      }),
      // Invalidate both the specific item and the list
      invalidatesTags: (result, error, { id }) => [
        { type: 'FAQ', id },
        { type: 'FAQ', id: 'LIST' },
      ],
    }),

    /**
     * Delete FAQ with optimistic update
     */
    deleteFAQ: builder.mutation({
      query: (id) => ({
        url: `/api/v1/faq/${id}/`,
        method: 'DELETE',
      }),
      // Invalidate both the specific item and the list
      invalidatesTags: (result, error, id) => [
        { type: 'FAQ', id },
        { type: 'FAQ', id: 'LIST' },
      ],
      // Optimistic update
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        // Optimistically update the cache
        const patchResult = dispatch(
          faqApi.util.updateQueryData('getFAQs', undefined, (draft) => {
            return draft.filter(faq => faq.id !== id);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          // Undo the optimistic update on error
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useGetFAQsQuery,
  useGetFAQByIdQuery,
  useCreateFAQMutation,
  useUpdateFAQMutation,
  useDeleteFAQMutation,
} = faqApi;
